// 共用策略詳情 modal（精靈許願詳情 + 我的精靈策略詳情合併）
//   以響應式版為基底；各頁傳入資料 + footer(動作鈕) + L + tfLabel + radarAxes。
//   L 需含 modal.*（標題/欄位）與 info.*（ⓘ tooltip）；mas-genie 用 shim 對應其 lang。
import { useState, useContext } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip as MuiTooltip,
} from "@mui/material";
import RadarChart6 from "../RadarChart6";
import { getAbilityBasis } from "../AbilityBasisInfo";
import { LanguageContext } from "context/LanguageContext";
import { COLORS, TYPO, RADIUS } from "theme/tokens";

// 簡易插值：fill('{n} 次', { n: 3 }) → '3 次'
const fill = (tpl, vars) =>
  Object.keys(vars || {}).reduce(
    (s, k) => s.replace(new RegExp(`\\{${k}\\}`, "g"), vars[k]),
    tpl || ""
  );

export default function StrategyDetailModal({
  open,
  onClose,
  L = {},
  title,
  typeLabel,
  typeColor,
  elite = {},
  radarValues,
  radarColor,
  radarKey,
  radarAxes,
  healthDisplay,
  healthColor,
  tfLabel,
  footer,
}) {
  const [kpiTab, setKpiTab] = useState("summary");

  // per-axis 能力依據（傳給雷達：點各軸標籤 → 跳該軸自己的 tooltip）
  const { language } = useContext(LanguageContext);
  const ab = getAbilityBasis(language);
  const axisInfo = Object.fromEntries((ab.items || []).map((it) => [it.key, { param: it.param, desc: it.desc }]));

  // 2026-09-14：期末考不到一年的策略不再顯示「預期年化獲利」。
  //   cagr =（1+期末考報酬）^(365/期末考天數)-1 —— LOW_FREQ 120 天是 3 次方、
  //   M5 21 天是 17 次方、M1 10 天是 36 次方；實測最誇張的一支 120 天 +30.8%
  //   被寫成年化 411%，抽卡頁就顯示「預期年化獲利 $41,140」。
  //   同時拿掉「annualReturn 缺值就用 monthlyReturn 自己複利」的後備路徑 ——
  //   那條路等於在前端再外推一次。
  const extrapolated = elite.annualIsExtrapolated === true;
  const apiAnnual = Number(elite.annualReturn);
  const annualPct = !extrapolated && Number.isFinite(apiAnnual) && apiAnnual > 0 ? apiAnnual : null;
  const yearProfit = annualPct != null ? Math.round((10000 * annualPct) / 100) : null;
  const examDays = Number.isFinite(Number(elite.examDays)) ? Number(elite.examDays) : null;
  const examRet = Number.isFinite(Number(elite.totalReturn)) ? Number(elite.totalReturn) : null;
  // 數值換算在後端（flattenElite），前端只挑模板；高頻策略否則會變「約 0 天一筆」
  const paceText = elite.tradePacePerDay != null
    ? (L.modal?.tradePaceDaily || "一天約 {n} 筆").replace("{n}", elite.tradePacePerDay)
    : (elite.tradePaceDays != null
        ? (L.modal?.tradePace || "約 {n} 天一筆").replace("{n}", elite.tradePaceDays)
        : null);
  // 1b（2026-09-14）：有「近 12 個月回測」就優先顯示 —— 每支都是同一個 365 天窗，
  //   彼此可比，也不必把 120/21/10 天的期末考複利外推。
  //   ⚠️ 這裡顯示的值必須與後端 cardScorePct 同源，否則銳/衡/守 與 🏆 徽章會與
  //   畫面數字脫節（masGenieRoutes 的 MOVE_BY_RANK 註解記載過這個事故）。
  const r12 = elite.recent12m || null;
  const isR12 = elite.cardBasis === "recent12m" && r12 && Number.isFinite(Number(r12.returnPct));
  const mdd = Number(elite.maxDrawdown);

  const tooltipProps = {
    arrow: true,
    enterTouchDelay: 0,
    leaveTouchDelay: 3000,
    componentsProps: {
      tooltip: { sx: { bgcolor: COLORS.lighter, color: COLORS.black, border: "1px solid #F36D26", fontFamily: 'var(--cjk-font, "Noto Sans TC"), sans-serif', fontSize: 13, fontWeight: 500, lineHeight: "21px", px: 1.5, py: 1, borderRadius: 2, maxWidth: 260, whiteSpace: "pre-line", textAlign: "left" } },
      arrow: { sx: { color: COLORS.lighter, "&::before": { border: "1px solid #F36D26" } } },
    },
  };
  const InfoBadge = ({ text }) => {
    const [hov, setHov] = useState(false);
    return (
      <MuiTooltip title={text || ""} {...tooltipProps}>
        <span
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{ display: "inline-flex", width: 24, height: 24, padding: 2, boxSizing: "border-box", justifyContent: "center", alignItems: "center", flexShrink: 0, cursor: "pointer" }}
        >
          <span style={{ display: "flex", width: 20, height: 20, justifyContent: "center", alignItems: "center", borderRadius: 20, background: hov ? COLORS.main : COLORS.grayLighter, transition: "background 0.15s" }}>
            <span style={{ color: hov ? COLORS.white : COLORS.grayDark, fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: "italic", fontWeight: 700, fontSize: 13, lineHeight: 1, transition: "color 0.15s" }}>i</span>
          </span>
        </span>
      </MuiTooltip>
    );
  };
  const infoTexts = {
    symbol: L.info?.symbol || "此策略主要交易的金融商品",
    timeframe: L.info?.timeframe || "策略下單依據的 K 線時間單位",
    tradeCount: L.info?.tradeCount || "策略每月平均進出場次數",
    health: L.info?.health || "後端評估訂閱策略的健康度（HIGH/MEDIUM/LOW/UNKNOWN）",
    mdd: L.info?.mdd || "歷史回測中最大資產回落幅度，越小越保守",
    sharpe: L.info?.sharpe || "風險調整後報酬，數值越大越好",
    sortino: L.info?.sortino || "只看下跌波動的報酬比，比夏普更貼近實際感受",
    pf: L.info?.pf || "總獲利 ÷ 總虧損，大於 1 代表整體獲利",
  };
  const INFO_ICON = {
    symbol: "/images/icon/genie/currency.png",
    timeframe: "/images/icon/genie/timeframe.png",
    tradeCount: "/images/icon/genie/avt.png",
    health: "/images/icon/genie/healthy.png",
    mdd: "/images/icon/genie/mdd.png",
  };
  const InfoRow = ({ label, value, infoKey }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2.5 text-slate-700">
        <img src={INFO_ICON[infoKey] || "/images/icon/genie/info-icon.png"} alt="" style={{ width: 18, height: 18, objectFit: "contain", flexShrink: 0 }} />
        <span className="font-bold font-sans text-sm">{label}</span>
        <InfoBadge text={infoTexts[infoKey] || ""} />
      </div>
      <span className="font-black">{value}</span>
    </div>
  );
  const blockBoxStyle = { border: `1px solid ${COLORS.grayLighter}`, borderRadius: RADIUS.xxl, padding: 20, background: COLORS.white };
  const barStyle = { display: "inline-block", width: 4, height: 21, background: COLORS.main, borderRadius: 20, flexShrink: 0 };
  const labelStyle = { ...TYPO.headingM, color: COLORS.black };
  const blockHeader = (text, mb = 16, extra = null) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: mb }}>
      <span style={barStyle} />
      <span style={labelStyle}>{text}</span>
      {extra}
    </div>
  );
  const tf = (v) => (typeof tfLabel === "function" ? tfLabel(v) : v);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 90, p: 3, boxSizing: "border-box", borderBottom: `1px solid ${COLORS.grayLighter}` }}>
        <span style={{ ...TYPO.modalTitle, color: COLORS.black }}>
          {title || L.modal?.fallbackTitle || "策略詳情"}
        </span>
        <button
          onClick={onClose}
          style={{ display: "flex", width: 40, height: 40, padding: 8, boxSizing: "border-box", justifyContent: "center", alignItems: "center", border: "none", background: "none", cursor: "pointer" }}
        >
          <span style={{ display: "flex", width: 24, height: 24, padding: 2, boxSizing: "border-box", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
            <img src="/images/icon/cross.png" alt={L.modal?.closeAlt || "關閉"} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </span>
        </button>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {/* 策略類型 chip */}
        <div style={{ ...blockBoxStyle, padding: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={barStyle} />
          <span style={labelStyle}>{L.modal?.strategyType || "策略類型"}</span>
          <span className="font-black text-white text-sm tracking-widest" style={{ background: typeColor, padding: "4px 16px", borderRadius: 8 }}>
            {typeLabel}
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* 左：策略概要 */}
          <div style={blockBoxStyle}>
            {blockHeader(L.modal?.summary || "策略概要")}
            <div className="text-center mb-5">
              <p style={{ ...TYPO.bodyM, color: COLORS.black, marginBottom: 4 }}>{isR12 ? (L.modal?.recentResult || "近 12 個月回測") : extrapolated ? (L.modal?.examResult || "期末考成績") : (L.modal?.estAnnual || "預估年化報酬")}</p>
              <p style={{ ...TYPO.numDisplayL, color: COLORS.main }}>
                {isR12
                  ? `${r12.returnPct > 0 ? "+" : ""}${Number(r12.returnPct).toFixed(1)}%`
                  : extrapolated
                    ? (examRet != null ? `${examRet > 0 ? "+" : ""}${examRet.toFixed(1)}%` : "—")
                    : (annualPct != null ? `+${annualPct.toFixed(0)}%` : "—")}
              </p>
              <p style={{ ...TYPO.bodyM, fontFamily: "Outfit, sans-serif", color: COLORS.black, marginTop: 8 }}>
                {isR12 ? (
                  (L.modal?.recentWindow || "實測 {a} ~ {b}").replace("{a}", r12.from || "—").replace("{b}", r12.to || "—")
                ) : extrapolated ? (
                  (L.modal?.examWindow || "實測 {n} 天，未年化").replace("{n}", examDays != null ? examDays : "—")
                ) : (
                  <>
                    {L.modal?.profitLine || "投入 $10,000，一年可獲利 "}
                    <span style={{ color: COLORS.main }}>
                      {yearProfit != null ? `$${yearProfit.toLocaleString()}` : "—"}
                    </span>
                  </>
                )}
              </p>
            </div>
            {/* 分頁切換：重點摘要 / 進階指標 */}
            <div style={{ display: "flex", gap: 4, padding: 4, background: COLORS.grayLightest, borderRadius: 10, marginBottom: 8 }}>
              {[
                { key: "summary", label: L.modal?.tabSummary || "重點摘要" },
                { key: "advanced", label: L.modal?.tabAdvanced || "進階指標" },
              ].map((t) => {
                const active = kpiTab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setKpiTab(t.key)}
                    style={{
                      flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
                      background: active ? COLORS.white : "transparent",
                      color: active ? COLORS.main : COLORS.grayDark,
                      boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                      ...TYPO.title, transition: "all 0.15s",
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 4 }}>
              {kpiTab === "summary" ? (
                <>
                  <InfoRow label={L.modal?.symbol || "交易標的"} infoKey="symbol"
                    value={<span className="font-black font-num text-xs" style={{ background: "#fff7ed", color: "#c2410c", padding: "4px 10px", borderRadius: 6 }}>{elite.symbol || "—"}</span>} />
                  <InfoRow label={L.modal?.timeframe || "時間框架"} infoKey="timeframe"
                    value={<span className="font-black text-slate-900 text-sm">{tf(elite.timeframe)}</span>} />
                  <InfoRow label={L.modal?.avgTrade || "月平均交易"} infoKey="tradeCount"
                    value={<span className="font-num font-black text-slate-900 text-sm">{elite.tradeCount != null ? fill(L.modal?.tradesUnit || "{n} 次", { n: elite.tradeCount }) : "—"}{paceText ? `（${paceText}）` : ""}</span>} />
                  <InfoRow label={L.modal?.health || "策略健康指數"} infoKey="health"
                    value={<span className="font-black text-sm" style={{ color: healthColor }}>{healthDisplay}</span>} />
                  <InfoRow label={L.modal?.mdd || "本金最大回撤"} infoKey="mdd"
                    value={<span className="font-num font-black text-sm" style={{ color: typeColor }}>{!Number.isNaN(mdd) ? `${mdd.toFixed(1)}%` : "—"}</span>} />
                </>
              ) : (
                <>
                  {elite.sharpeRatio != null && (
                    <InfoRow label={L.modal?.sharpe || "夏普比率"} infoKey="sharpe"
                      value={<span className="font-num font-black text-slate-900 text-sm">{Number(elite.sharpeRatio).toFixed(2)}</span>} />
                  )}
                  {elite.sortinoRatio != null && (
                    <InfoRow label={L.modal?.sortino || "索提諾比率"} infoKey="sortino"
                      value={<span className="font-num font-black text-slate-900 text-sm">{Number(elite.sortinoRatio).toFixed(2)}</span>} />
                  )}
                  {elite.profitFactor != null && (
                    <InfoRow label={L.modal?.profitFactor || "獲利因子"} infoKey="pf"
                      value={<span className="font-num font-black text-slate-900 text-sm">{Number(elite.profitFactor).toFixed(2)}</span>} />
                  )}
                </>
              )}
            </div>
          </div>

          {/* 右：策略特徵分析 */}
          <div style={blockBoxStyle}>
            {blockHeader(L.modal?.features || "策略特徵分析", 8)}
            <RadarChart6
              values={radarValues}
              color={radarColor}
              variant="full"
              height={320}
              instanceKey={radarKey}
              axes={radarAxes}
              axisInfo={axisInfo}
              basedOn={ab.basedOn}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions sx={{ p: "24px 24px 0 24px", minHeight: 96, boxSizing: "border-box", gap: 1.5, borderTop: `1px solid ${COLORS.grayLighter}`, background: COLORS.white, alignItems: "flex-start" }}>
        {footer}
      </DialogActions>
    </Dialog>
  );
}

StrategyDetailModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  L: PropTypes.object,
  title: PropTypes.node,
  typeLabel: PropTypes.node,
  typeColor: PropTypes.string,
  elite: PropTypes.object,
  radarValues: PropTypes.any,
  radarColor: PropTypes.string,
  radarKey: PropTypes.string,
  healthDisplay: PropTypes.node,
  healthColor: PropTypes.string,
  tfLabel: PropTypes.func,
  footer: PropTypes.node,
};
